import { useEffect, useMemo } from 'react';
import { useMachineContext } from '../../../pages/machine/machine-context-provider';
import { useRobotManufacturerContext } from '../../../pages/machine/robot-manufacturer-context';
import { FanucOperations } from '../../../robot/manufacturers/fanuc/fanuc-operations';
import { UROperations } from '../../../robot/manufacturers/universal-robots/ur-operations';
import { RobotManufacturer, RobotOperations } from '../../../robot/robot.types';
import { useCacheWriterBindingLazy } from './cache-writer.binding';
import { useScriptActionBindingLazy } from './script-action.binding';

/**
 * Hook that provides robot operations based on the current manufacturer context
 * Abstracts away script execution and provides high-level robot operations
 * Returns properly typed operations based on the manufacturer context
 */
export const useRobotOperations = (): RobotOperations => {
  const { machine } = useMachineContext();
  const { manufacturer } = useRobotManufacturerContext();

  // One-shot binding for operations that execute once and complete
  // (moveToPoint, halt, stopProgram)
  const { send: sendScriptOneShot } = useScriptActionBindingLazy({
    closeImmediately: true,
  });

  // Long-running binding for operations that need to stay active
  // (startFineTune - keeps binding open for subsequent fineTuneMoveTo calls)
  const { send: sendScriptLongRunning, close: closeScriptBinding } =
    useScriptActionBindingLazy({
      closeImmediately: false,
    });

  const writeToCache = useCacheWriterBindingLazy();

  // Initialize cache writer binding
  useEffect(() => {
    writeToCache(machine?.id, {
      requests: [],
    });
  }, [writeToCache, machine?.id]);

  const operations = useMemo(() => {
    switch (manufacturer) {
      case RobotManufacturer.UNIVERSAL_ROBOTS:
        return new UROperations(
          sendScriptOneShot,
          sendScriptLongRunning,
          closeScriptBinding,
          writeToCache
        );
      case RobotManufacturer.FANUC:
        return new FanucOperations(writeToCache);
      default:
        throw new Error(
          `Operations not implemented for manufacturer: ${manufacturer}`
        );
    }
  }, [
    manufacturer,
    sendScriptOneShot,
    sendScriptLongRunning,
    closeScriptBinding,
    writeToCache,
  ]);

  return operations;
};

/**
 * Hook that provides lazy robot operations initialization
 * Useful when you don't need operations immediately on mount
 * Returns a factory function that creates properly typed operations
 */
export const useRobotOperationsLazy = () => {
  const { machine } = useMachineContext();
  const { manufacturer } = useRobotManufacturerContext();

  // One-shot binding for operations that execute once and complete
  const { send: sendScriptOneShot } = useScriptActionBindingLazy({
    closeImmediately: true,
  });

  // Long-running binding for operations that need to stay active
  const { send: sendScriptLongRunning, close: closeScriptBinding } =
    useScriptActionBindingLazy({
      closeImmediately: false,
    });

  const writeToCache = useCacheWriterBindingLazy();

  // Initialize cache writer binding
  useEffect(() => {
    writeToCache(machine?.id, {
      requests: [],
    });
  }, [writeToCache, machine?.id]);

  const createOperations = useMemo(() => {
    return (): RobotOperations => {
      switch (manufacturer) {
        case RobotManufacturer.UNIVERSAL_ROBOTS:
          return new UROperations(
            sendScriptOneShot,
            sendScriptLongRunning,
            closeScriptBinding,
            writeToCache
          );
        default:
          throw new Error(
            `Operations not implemented for manufacturer: ${manufacturer}`
          );
      }
    };
  }, [
    manufacturer,
    sendScriptOneShot,
    sendScriptLongRunning,
    closeScriptBinding,
    writeToCache,
  ]);

  return createOperations;
};
